import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Search, X, CornerDownLeft, ArrowUp, ArrowDown } from 'lucide-react';
import { SEARCH_INDEX, scoreMatch } from './docsSearchIndex';

const KIND_STYLES = {
  page:    { label: 'Page',    className: 'bg-[#1F4E79] text-white' },
  section: { label: 'Section', className: 'bg-[#2d6ca8] text-white' },
  field:   { label: 'Field',   className: 'bg-[#85ea2d] text-gray-900' },
  code:    { label: 'Code',    className: 'bg-[#c32222] text-white' },
};

/**
 * Command palette for the Technical Documentation hub.
 * Trigger: Cmd/Ctrl + K (or the Search button in the Header)
 */
const DocsSearch = ({ open, onClose }) => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const [query, setQuery] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(0);

  // Focus input + reset state when the dialog opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setQuery('');
      setSelectedIdx(0);
    }
  }, [open]);

  // Compute top results (max 12) scored by relevance
  const results = useMemo(() => {
    if (!query.trim()) {
      // Show a curated "quick links" set when empty
      return SEARCH_INDEX.filter((e) => e.kind === 'page').slice(0, 8);
    }
    return SEARCH_INDEX
      .map((e) => ({ entry: e, score: scoreMatch(e, query) }))
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 12)
      .map((x) => x.entry);
  }, [query]);

  // Keep selection in range when results list changes
  useEffect(() => {
    setSelectedIdx(0);
  }, [query]);

  // Scroll selected row into view
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${selectedIdx}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [selectedIdx, results.length]);

  const navigateTo = (entry) => {
    if (!entry) return;
    onClose();
    // Append the search query as `?q=` so the destination page can highlight matches
    const cleanQuery = query.trim();
    const sep = entry.path.includes('?') ? '&' : '?';
    const pathWithQuery = cleanQuery
      ? entry.path.includes('#')
        ? entry.path.replace('#', `${sep}q=${encodeURIComponent(cleanQuery)}#`)
        : `${entry.path}${sep}q=${encodeURIComponent(cleanQuery)}`
      : entry.path;
    navigate(pathWithQuery);
    // If the path contains a hash, scroll after the route mounts
    const hashIdx = entry.path.indexOf('#');
    if (hashIdx > -1) {
      const id = entry.path.slice(hashIdx + 1);
      setTimeout(() => {
        const target = document.getElementById(id);
        if (target) {
          const y = target.getBoundingClientRect().top + window.scrollY - 100;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 250);
    } else {
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      navigateTo(results[selectedIdx]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-start justify-center pt-24 px-4"
      data-testid="docs-search-modal"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Dialog */}
      <div className="relative w-full max-w-xl bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-200 flex flex-col">
        {/* Search input row */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200">
          <Search size={20} className="text-gray-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search pages, sections, fields, error codes…"
            className="flex-1 bg-transparent outline-none text-gray-900 placeholder:text-gray-400 text-base"
            data-testid="docs-search-input"
          />
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-700 transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Results list */}
        <div
          ref={listRef}
          className="flex-1 overflow-y-auto max-h-[50vh]"
          data-testid="docs-search-results"
        >
          {results.length === 0 ? (
            <div className="px-5 py-10 text-center text-gray-500 text-sm">
              No results for <span className="font-mono text-gray-700">"{query}"</span>
            </div>
          ) : (
            <ul>
              {results.map((entry, idx) => {
                const kindStyle = KIND_STYLES[entry.kind] || KIND_STYLES.page;
                const isActive = idx === selectedIdx;
                return (
                  <li key={`${entry.path}-${idx}`}>
                    <button
                      type="button"
                      data-idx={idx}
                      onMouseEnter={() => setSelectedIdx(idx)}
                      onClick={() => navigateTo(entry)}
                      className={`w-full text-left px-5 py-3 flex items-start gap-3 border-l-2 transition-colors ${
                        isActive
                          ? 'bg-blue-50 border-[#1F4E79]'
                          : 'border-transparent hover:bg-gray-50'
                      }`}
                      data-testid={`search-result-${idx}`}
                    >
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide flex-shrink-0 mt-0.5 ${kindStyle.className}`}>
                        {kindStyle.label}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-semibold truncate ${isActive ? 'text-[#1F4E79]' : 'text-gray-900'}`}>
                          {entry.title}
                        </div>
                        {entry.subtitle && (
                          <div className="text-xs text-gray-500 truncate mt-0.5">{entry.subtitle}</div>
                        )}
                      </div>
                      {isActive && <CornerDownLeft size={14} className="text-[#1F4E79] flex-shrink-0 mt-1" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer with keyboard hints */}
        <div className="flex items-center justify-between px-5 py-2 border-t border-gray-200 bg-gray-50/60 text-[11px] text-gray-500">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded font-mono shadow-sm flex items-center"><ArrowUp size={10} /></kbd>
              <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded font-mono shadow-sm flex items-center"><ArrowDown size={10} /></kbd>
              Navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded font-mono shadow-sm">Enter</kbd>
              Open
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded font-mono shadow-sm">Esc</kbd>
              Close
            </span>
          </div>
          <span className="hidden sm:inline">eCOMM Docs Search</span>
        </div>
      </div>
    </div>,
    document.body
  );
};

/**
 * Self-contained trigger button that shows only on /technical-documentation/* routes.
 * Opens the modal on click or via Cmd/Ctrl+K anywhere on those pages.
 */
export const DocsSearchTrigger = () => {
  const [open, setOpen] = useState(false);
  const [isMac, setIsMac] = useState(false);

  useEffect(() => {
    setIsMac(typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform));
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const isCmdK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
      if (isCmdK) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-md text-white text-sm transition-colors"
        data-testid="docs-search-trigger"
      >
        <Search size={14} />
        <span className="hidden sm:inline">Search docs…</span>
        <kbd className="hidden md:inline-flex items-center px-1.5 py-0.5 bg-white/20 border border-white/20 rounded text-[10px] font-mono ml-1">
          {isMac ? '⌘' : 'Ctrl'}+K
        </kbd>
      </button>
      <DocsSearch open={open} onClose={() => setOpen(false)} />
    </>
  );
};

export default DocsSearch;
